import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  FormControlLabel,
  Checkbox,
  FormGroup,
  RadioGroup,
  Radio,
  Grid,
  FormControl,
  InputAdornment,
  Popper,
  Paper,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemText,
  FormHelperText,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useState, useEffect } from "react";
import api from "../../services/ApiService";
import { ApiResponse } from "../../models/ApiResponse";
import { User } from "../../models/User";
import { KeyValues, ProductFilterOptions } from "../../models/Items/FilterOptions";
import { sortByKey } from "../../utils/helpers";

const UserModal = ({
  closeModal,
  isModalOpen,
  userEditOpen,
  handleRadioChange,
  formik,
}) => {
  const [userRoleOptions, setUserRoleOptions] = useState<User[]>([]);
  const [brandCollectionOptions, setBrandCollectionOptions] = useState<KeyValues[]>([]);
  const [brandCollectionOpen, setBrandCollectionOpen] = useState(false);
  const [brandCollectionAnchorEl, setBrandCollectionAnchorEl] = useState<HTMLElement | null>(null);
  const [brandCollectionSearch, setBrandCollectionSearch] = useState("");

  const getRoleList = () => {
    try {
      api
        .get<ApiResponse<User[]>>(`/Role/list`)
        .then((response: ApiResponse<User[]>) => {
          if (response.isSuccess) {
            setUserRoleOptions(response.data);
          } else {
            throw new Error(response.message);
          }
        })
        .catch((error) => {
          console.error("Exception from Role List", error);
        });
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const getBrandCollectionOptions = async () => {
    try {
      const response = await api.get<ApiResponse<ProductFilterOptions>>(`/Item/filter-options`);
      if (response.isSuccess) {
        setBrandCollectionOptions(response.data?.brands || []);
      }
    } catch (error: any) {
      console.error("Exception from Brand Collection options", error);
    }
  };

  useEffect(() => {
    getRoleList();
    getBrandCollectionOptions();
  }, []);

  const rolesCheckboxRender = () => {
    const userRoleOptionsSorted = [...userRoleOptions].sort((a, b) =>
      a?.name?.localeCompare(b?.name)
    );

    return userRoleOptionsSorted.map((value) => (
      <div key={value.id}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formik.values.roles.includes(String(value.id))}
              onChange={() => {
                const roles = formik.values.roles;
                const roleId = String(value.id);

                if (roles.includes(roleId)) {
                  const updatedRoles = roles.filter((role) => role !== roleId);
                  formik.setFieldValue("roles", updatedRoles);
                } else {
                  formik.setFieldValue("roles", [...roles, roleId]);
                }
              }}
              name={`roles.${value.id}`}
            />
          }
          label={value.name}
        />
      </div>
    ));
  };

  const getOptionText = (option: KeyValues) =>
    option?.name || option?.description || "";

  const selectedBrandCollections = brandCollectionOptions.filter((option) =>
    formik.values.brandCollections?.includes(option.id)
  );

  const selectedIds = new Set(formik.values.brandCollections || []);

  const getSortedValues = (arr: KeyValues[]) => {
    if (!arr?.length) return "";
    return [...arr]
      .sort(sortByKey("name"))
      .map((item) => getOptionText(item))
      .join(", ");
  };

  const searchTerm = brandCollectionSearch.trim().toLowerCase();
  const filteredBrandCollections = searchTerm
    ? brandCollectionOptions.filter((opt) =>
        getOptionText(opt).toLowerCase().includes(searchTerm)
      )
    : brandCollectionOptions;

  const sortedFilteredBrandCollections = [...filteredBrandCollections].sort(
    sortByKey("name")
  );

  const closeBrandCollectionDropdown = () => {
    setBrandCollectionOpen(false);
    setBrandCollectionAnchorEl(null);
    setBrandCollectionSearch("");
    formik.setFieldTouched("brandCollections", true);
  };

  const handleBrandCollectionToggle = (option: KeyValues) => {
    const id = option.id;
    const checked = selectedIds.has(id);
    const next = checked
      ? (formik.values.brandCollections || []).filter((x: number) => x !== id)
      : [...(formik.values.brandCollections || []), id];
    formik.setFieldValue("brandCollections", next);
  };

  return (
    <Dialog open={isModalOpen} onClose={closeModal} fullWidth>
      <DialogActions>
        <Button onClick={closeModal}>x</Button>
      </DialogActions>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                autoFocus
                margin="dense"
                id="name"
                label={formik.errors.name ? formik.errors.name : "Enter Name"}
                type="text"
                fullWidth
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                margin="dense"
                id="email"
                label={formik.errors.email ? formik.errors.email : "Enter Email"}
                type="text"
                fullWidth
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
                error={formik.touched.email && Boolean(formik.errors.email)}
              />
            </Grid>
            <Grid item xs={6}>
              <h4>Set Roles</h4>
              <FormGroup>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flexWrap: "wrap",
                    maxHeight: "300px",
                    overflowX: "auto",
                  }}
                >
                  {rolesCheckboxRender()}
                </div>
              </FormGroup>
            </Grid>
            <Grid item xs={6}>
              <FormControl style={{ marginTop: 8, width: "100%" }}>
                <label
                  htmlFor="outlined-brandCollections"
                  style={{ textTransform: "capitalize", marginBottom: 4 }}
                >
                  Brand Collection
                  {formik.values.availableForAssignments && (
                    <span className="required-field-asterisk-icon">{` *`}</span>
                  )}
                </label>
                <TextField
                  id="outlined-brandCollections"
                  className="truncated-dropdown-value"
                  placeholder="- Select -"
                  value={
                    selectedBrandCollections?.length
                      ? getSortedValues([...selectedBrandCollections])
                      : ""
                  }
                  onClick={(e) => {
                    setBrandCollectionAnchorEl(e.currentTarget as HTMLElement);
                    setBrandCollectionOpen(true);
                  }}
                  error={
                    formik.touched.brandCollections &&
                    Boolean(formik.errors.brandCollections)
                  }
                  inputProps={{
                    readOnly: true,
                    "aria-readonly": true,
                    title: selectedBrandCollections?.length
                      ? getSortedValues([...selectedBrandCollections])
                      : "",
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <ArrowDropDownIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                {formik.touched.brandCollections && formik.errors.brandCollections && (
                  <FormHelperText sx={{ marginLeft: 0 }} error>
                    {formik.errors.brandCollections}
                  </FormHelperText>
                )}
                <Popper
                  open={brandCollectionOpen}
                  anchorEl={brandCollectionAnchorEl}
                  placement="bottom-start"
                  style={{ zIndex: 1400 }}
                  modifiers={[
                    { name: "offset", options: { offset: [0, 6] } },
                    {
                      name: "sameWidth",
                      enabled: true,
                      phase: "beforeWrite",
                      requires: ["computeStyles"],
                      fn: ({ state }) => {
                        state.styles.popper.width = `${state.rects.reference.width}px`;
                      },
                    },
                  ]}
                >
                  <ClickAwayListener onClickAway={closeBrandCollectionDropdown}>
                    <Paper
                      elevation={4}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          closeBrandCollectionDropdown();
                        }
                      }}
                      style={{ padding: 8 }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search here"
                        value={brandCollectionSearch}
                        autoFocus
                        onChange={(e) => setBrandCollectionSearch(e.target.value)}
                        sx={{ mb: 1 }}
                      />
                      <List dense sx={{ maxHeight: 320, overflow: "auto" }}>
                        {sortedFilteredBrandCollections.length === 0 ? (
                          <ListItemText
                            primary="No data found"
                            sx={{ px: 2, py: 1, color: "text.secondary" }}
                          />
                        ) : (
                          sortedFilteredBrandCollections.map((opt) => {
                            const checked = selectedIds.has(opt.id);
                            return (
                              <ListItemButton
                                key={opt.id}
                                selected={checked}
                                onClick={() => handleBrandCollectionToggle(opt)}
                              >
                                <ListItemText primary={getOptionText(opt)} />
                              </ListItemButton>
                            );
                          })
                        )}
                      </List>
                    </Paper>
                  </ClickAwayListener>
                </Popper>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!formik.values.availableForAssignments}
                    onChange={(event) => {
                      formik.setFieldValue(
                        "availableForAssignments",
                        event.target.checked
                      );
                    }}
                    name="availableForAssignments"
                  />
                }
                label="Can Have Assignments"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!formik.values.canBypassSkuValidation}
                    onChange={(event) => {
                      formik.setFieldValue(
                        "canBypassSkuValidation",
                        event.target.checked
                      );
                    }}
                    name="canBypassSkuValidation"
                  />
                }
                label="Can bypass JDE SKU Validation"
              />
            </Grid>
          </Grid>
          <h4>Set Status</h4>
          <RadioGroup
            row
            aria-labelledby="demo-row-radio-buttons-group-label"
            name="newRoleStatus"
            value={formik.values.newRoleStatus}
            onChange={(event) => {
              formik.handleChange(event);
              handleRadioChange(event);
            }}
          >
            <FormControlLabel
              value={true}
              control={<Radio />}
              label="Active"
            />
            <FormControlLabel
              value={false}
              control={<Radio />}
              label="Inactive"
            />
          </RadioGroup>
          <DialogActions>
            <Button variant="contained" type="submit">
              {userEditOpen ? "Update User" : "Add User"}
            </Button>
          </DialogActions>
        </DialogContent>
      </form>
    </Dialog>
  );
};

export default UserModal;
